import { API_ORIGIN, apiUrl } from "./apiOrigin";
import { VERSION as CRITERIA_VERSION, catalogue as JURISDICTION_CATALOGUE, rowApplicability, locationOf, detailsFor, decorateGroups, badge } from './complianceJurisdiction';
// =============================================================================
// ComplianceOverview.js — the group manager's Overview page (2026-10).
//
// One screen that shows where the group is without clicking anything:
//   1. headline numbers        4. coming up (renewals, next 90 days)
//   2. done / to do by branch  5. who has open requests
//   3. branch × area heatmap   6. map of the branches
//   7. % in place over time (one figure per branch per day)
//   8. a printable / emailable summary (group or one branch)
// Clicking a piece opens the Outstanding list, already filtered to it.
//
// Kept in its own file so it can be changed without touching the Working
// grid. It reads exactly the same statuses as the grid, through the pure
// functions ComplianceWorkingGrid exports (cellViewOf, buildOutstanding…),
// and the same branch / area percentages (the page's rollup).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { cellViewOf, buildOutstanding, liveModuleFrom, liveStatusFrom, groupsFor, OutstandingDialog, OUT_TYPES, PARTS_OF } from "./ComplianceWorkingGrid";
const API_BASE = apiUrl("");
const GRID = `${API_BASE}/api/headoffice/compliance-grid`;
const DAY = 86400000;
async function post(token, url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {})
    },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const esc = v => String(v ?? "").replace(/[&<>"']/g, c => ({
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;"
})[c]);
const round = n => n == null || Number.isNaN(n) ? null : Math.round(n);
const mean = a => {
  const v = a.filter(x => x != null && !Number.isNaN(x));
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
};

// Status → one of five buckets (n/a is left out of the totals).
const BUCKETS = [{
  k: "done",
  label: "In place",
  color: "#389E0D",
  type: null
}, {
  k: "due",
  label: "Renewal due",
  color: "#FAAD14",
  type: "due"
}, {
  k: "waiting",
  label: "Waiting on staff",
  color: "#FA8C16",
  type: "awaiting"
}, {
  k: "missing",
  label: "Missing or expired",
  color: "#CF1322",
  type: "all"
}, {
  k: "unset",
  label: "Not set up",
  color: "#BFBFBF",
  type: "unset"
}];
const BUCKET_OF = {
  current: "done",
  "pharmace-ok": "done",
  linked: "done",
  "due-soon": "due",
  "needs-expiry": "due",
  awaiting: "waiting",
  chasing: "waiting",
  expired: "missing",
  missing: "missing",
  "pharmace-no": "missing",
  "pharmace-never": "missing",
  "pharmace-lapsed": "missing",
  "log-gaps": "missing",
  "log-lapsed": "missing",
  pharmace: "missing",
  unset: "unset"
};
const band = p => p == null ? {
  bg: "#F5F5F5",
  fg: "#8C8C8C"
} : p >= 85 ? {
  bg: "#F6FFED",
  fg: "#237804"
} : p >= 65 ? {
  bg: "#FFF7E6",
  fg: "#AD4E00"
} : {
  bg: "#FFF1F0",
  fg: "#A8071A"
};
const dotColor = p => p == null ? "#8C8C8C" : p >= 85 ? "#389E0D" : p >= 65 ? "#FA8C16" : "#CF1322";
const SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];

// Every data row (parts listed separately, rows added in the Working grid included).
function rowsOf(groups, customs) {
  const out = [];
  for (const g of groups) for (const it of g.items || []) {
    const common = extra => ({
      dsp: !!g.dsp,
      groupId: g.id,
      itemId: it.id,
      groupName: g.name,
      itemName: it.name,
      ...extra
    });
    for (const el of it.elements || []) {
      if (PARTS_OF[el.id]) for (const p of PARTS_OF[el.id]) out.push(common({
        rowId: `el:${p.id}`,
        elementId: p.id,
        label: `${el.label} — ${p.label}`,
        part: true
      }));else out.push(common({
        rowId: `el:${el.id}`,
        elementId: el.id,
        label: el.label
      }));
    }
    for (const c of customs.filter(x => x.groupId === g.id && x.itemId === it.id)) out.push(common({
      ...c
    }));
  }
  return out;
}

// -----------------------------------------------------------------------------
// The panels after "Done and still to do, by branch" are optional (2026-10):
// Regulatory Compliance shows them only when ticked under "Would you like more
// views of current status?" (all off by default). `views` is a Set of keys from
// OVERVIEW_VIEWS; leave it out to show everything.
export const OVERVIEW_VIEWS = [{
  key: "map",
  label: "Where the branches are"
}, {
  key: "areas",
  label: "Branch by area"
}, {
  key: "coming",
  label: "Coming up (90 days)"
}, {
  key: "who",
  label: "Who has open requests"
}, {
  key: "trend",
  label: "% in place over time"
}];
export default function ComplianceOverview({
  groups: baseGroups,
  token,
  liveStatus,
  liveModule: rawLiveModule,
  rollup,
  displayName = n => n,
  masked = false,
  ready = true,
  renderDetail = null,
  views = null
}) {
  const shows = k => !views || views.has(k);
  const [dataView, setDataView] = useState(null); // { row, branch } — "Show the data behind this"
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [mhra, setMhra] = useState([]);
  const [error, setError] = useState("");
  const [out, setOut] = useState(null); // Outstanding list, with its starting filters
  const [summary, setSummary] = useState(false);
  const [range, setRange] = useState(90);
  const load = useCallback(async () => {
    setError("");
    try {
      const d = await post(token, `${GRID}/load`, {});
      setData(d);
      post(token, `${GRID}/overview/meta`, {
        days: 400
      }).then(setMeta).catch(e => setMeta({
        branches: [],
        history: [],
        error: e.message
      }));
      post(token, `${API_BASE}/api/headoffice/mhra/unread`, {
        dbNames: (d.branches || []).map(b => b.dbName)
      }).then(r => setMhra(r.rows || [])).catch(() => setMhra([]));
    } catch (e) {
      setError(e.message);
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);

  // ---- the same model as the Working grid ----
  const branches = useMemo(() => data?.branches || [], [data]);
  const sites = useMemo(() => data?.sites || {}, [data]);
  const isDsp = useCallback(b => !!sites[b.dbName]?.dsp, [sites]);
  const groups = useMemo(() => groupsFor(baseGroups, branches.some(isDsp), branches, sites), [baseGroups, branches, isDsp]);
  const liveModule = useMemo(() => liveModuleFrom(rawLiveModule), [rawLiveModule]);
  const liveStatusX = useMemo(() => liveStatusFrom(data, liveStatus), [data, liveStatus]);
  const cells = useMemo(() => new Map((data?.cells || []).map(c => [`${c.rowId}|${c.dbName}`, c])), [data]);
  const rowCfg = useMemo(() => new Map((data?.rows || []).map(r => [r.rowId, r])), [data]);
  const cellView = useCallback((row, b) => cellViewOf(row, b, {
    cells,
    rowCfg,
    liveModule,
    liveStatusX,
    data
  }), [cells, rowCfg, liveModule, liveStatusX, data]);
  const outstanding = useMemo(() => data ? buildOutstanding({
    data,
    groups,
    branches,
    isDsp,
    cellView
  }) : [], [data, groups, branches, isDsp, cellView]);
  // Every item with what holds it (the inspector view) — only while the list is open.
  const evidenceAll = useMemo(() => out && data ? buildOutstanding({
    data,
    groups,
    branches,
    isDsp,
    cellView,
    all: true
  }) : null, [out, data, groups, branches, isDsp, cellView]);
  const rows = useMemo(() => rowsOf(groups, (data?.rows || []).filter(r => r.custom)), [groups, data]);
  const name = useCallback(b => `${displayName(b.pharmacyName, masked)} · ${badge(locationOf(b, data))}`, [displayName, masked, data]);

  // ---- per branch: buckets, %, renewals, open requests ----
  const model = useMemo(() => {
    if (!data) return null;
    const now = Date.now();
    const per = branches.map(b => {
      const buckets = {
        done: 0,
        due: 0,
        waiting: 0,
        missing: 0,
        unset: 0
      };
      const renewals = [];
      for (const row of rows) {
        if (row.dsp && !isDsp(b)) continue;
        const v = cellView(row, b);
        if (v.applicable === false) continue;
        const k = BUCKET_OF[v.status];
        if (k) buckets[k]++;
        const c = v.c;
        const exp = c?.expiresAt && v.source !== "team" ? new Date(c.expiresAt).getTime() : null;
        if (exp && exp - now <= 90 * DAY && exp - now >= -60 * DAY) renewals.push({
          when: exp,
          label: row.label,
          groupName: row.groupName,
          nobody: !c.recipientStaffId && (v.source === "task" || v.source === "upload")
        });
        for (const p of c?.team?.people || []) {
          const e = p.expiresAt ? new Date(p.expiresAt).getTime() : null;
          if (e && e - now <= 90 * DAY && e - now >= -60 * DAY) renewals.push({
            when: e,
            label: `Professional registration — ${p.name}`,
            groupName: row.groupName,
            nobody: false
          });
        }
      }
      const groupPct = {};
      for (const g of groups) {
        const p = rollup?.pct(g.id, null, b.pharmacyName);
        if (p != null) groupPct[g.id] = p;
      }
      const pct = mean(Object.values(groupPct));
      const total = Object.values(buckets).reduce((s, x) => s + x, 0);
      return {
        b,
        buckets,
        total,
        pct,
        groupPct,
        renewals: renewals.sort((x, y) => x.when - y.when),
        chase: outstanding.filter(x => x.branch.dbName === b.dbName).length + mhra.filter(m => m.dbName === b.dbName).length
      };
    });
    // Open requests by person (overdue = past its due date).
    const due = x => x.cell?.task?.dueDate || x.person?.task?.dueDate || x.mhraRow?.dueDate || null;
    const reqs = [...outstanding.filter(x => x.type === "awaiting" && x.staffName && x.staffName !== "Staff at the branch"), ...mhra.map(m => ({
      staffName: m.staffName,
      branch: branches.find(b => b.dbName === m.dbName),
      mhraRow: m
    }))].filter(x => x.branch);
    const byPerson = new Map();
    let overdue = 0,
      oldest = 0;
    for (const x of reqs) {
      const d = due(x),
        late = d && new Date(d).getTime() < now;
      if (late) {
        overdue++;
        oldest = Math.max(oldest, Math.round((now - new Date(d).getTime()) / DAY));
      }
      const k = `${x.staffName}|${x.branch.dbName}`;
      const e = byPerson.get(k) || {
        staffName: x.staffName,
        branch: x.branch,
        open: 0,
        late: 0
      };
      e.open++;
      if (late) e.late++;
      byPerson.set(k, e);
    }
    const people = [...byPerson.values()].sort((a, b) => b.late - a.late || b.open - a.open || a.staffName.localeCompare(b.staffName));
    const in30 = per.flatMap(p => p.renewals.filter(r => r.when >= now && r.when - now <= 30 * DAY));
    return {
      per,
      people,
      overdue,
      oldest,
      toChase: outstanding.length + mhra.length,
      renewals30: in30.length,
      renewals30Nobody: in30.filter(r => r.nobody).length,
      groupPct: mean(per.map(p => p.pct))
    };
  }, [data, branches, rows, isDsp, cellView, groups, rollup, outstanding, mhra]);

  // ---- trend: saved daily figures + today's ----
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London"
  }).format(new Date());
  const trend = useMemo(() => {
    if (!model) return {
      points: [],
      change: null
    };
    const hist = meta?.history || [];
    const days = [...new Set([...hist.map(h => h.day), today])].sort().filter(d => new Date(d) >= new Date(Date.now() - range * DAY));
    const val = new Map(hist.map(h => [`${h.day}|${h.dbName}`, h.pct]));
    for (const p of model.per) if (p.pct != null) val.set(`${today}|${p.b.dbName}`, round(p.pct * 10) / 10);
    const points = days.map(d => {
      const o = {
        day: d,
        label: new Date(d).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short"
        })
      };
      const vs = [];
      for (const p of model.per) {
        const v = val.get(`${d}|${p.b.dbName}`);
        if (v != null) {
          o[p.b.dbName] = v;
          vs.push(v);
        }
      }
      if (vs.length) o.group = Math.round(mean(vs) * 10) / 10;
      return o;
    });
    // Change against the closest saved day at least 30 days ago.
    const past = [...new Set(hist.map(h => h.day))].sort().filter(d => new Date(d) <= new Date(Date.now() - 30 * DAY)).pop();
    const pastVals = past ? hist.filter(h => h.day === past).map(h => h.pct) : [];
    return {
      points,
      change: past && model.groupPct != null ? model.groupPct - mean(pastVals) : null,
      past
    };
  }, [model, meta, range, today]);

  // ---- save today's figures (once the live data has settled) ----
  // The figures must stay the same for 6 seconds before they're saved, so a
  // half-loaded page never records a dip.
  const savedRef = useRef(false),
    modelRef = useRef(null);
  modelRef.current = model;
  const snapshotKey = model ? JSON.stringify(model.per.map(p => [p.b.dbName, round(p.pct), p.buckets])) : "";
  useEffect(() => {
    const m = modelRef.current;
    if (!m || !ready || savedRef.current || m.per.every(p => p.pct == null)) return undefined;
    const t = setTimeout(() => {
      savedRef.current = true;
      post(token, `${GRID}/overview/snapshot`, {
        criteriaVersion: CRITERIA_VERSION,
        rows: m.per.filter(p => p.pct != null).map(p => ({
          dbName: p.b.dbName,
          pct: p.pct,
          groups: p.groupPct,
          buckets: p.buckets
        }))
      }).catch(() => {
        savedRef.current = false;
      });
    }, 6000);
    return () => clearTimeout(t);
  }, [snapshotKey, ready, token]);
  const openOut = (opts = {}) => setOut(opts);
  const card = {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 10,
    padding: "12px 14px"
  };
  const h = {
    fontSize: 13,
    fontWeight: 700,
    color: "#262626",
    margin: "0 0 8px"
  };
  const sub = {
    fontSize: 12,
    color: "#5D5D5D"
  };
  if (error) return <div style={{
    marginTop: 16,
    color: "#A8071A"
  }}>Couldn’t load the overview: {error}</div>;
  if (!model) return <div style={{
    marginTop: 16,
    color: "#5D5D5D"
  }}>Loading the overview…</div>;
  const kpi = (label, value, note, color, onClick) => <button type="button" onClick={onClick} disabled={!onClick} style={{
    ...card,
    textAlign: "left",
    cursor: onClick ? "pointer" : "default",
    font: "inherit"
  }}>
      <div style={sub}>{label}</div>
      <div style={{
      fontSize: 28,
      fontWeight: 700,
      color: color || "#262626",
      lineHeight: 1.2
    }}>{value}</div>
      <div style={{
      ...sub,
      fontSize: 11
    }}>{note}</div>
    </button>;
  const ch = trend.change;
  return <div style={{
    marginTop: 16
  }}>
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 12
    }}>
        <div style={{
        flex: 1,
        ...sub
      }}>Everything on this page is live. Click any bar, square, dot or name to see the items behind it.{!ready && " Live Pharmace data is still loading — figures will settle in a moment."}</div>
        <button onClick={() => openOut({
        mode: "all",
        sort: "item"
      })} title="Everything held for every item at every branch — documents, who has read & signed them, requests and history. Ready for an inspector.">🔍 Evidence (inspector view)</button>
        <button onClick={() => openOut({})}>⚑ Outstanding · {model.toChase}</button>
        <button onClick={() => setSummary(true)} style={{
        fontWeight: 700,
        background: "#0958D9",
        color: "#fff",
        border: "none",
        borderRadius: 6,
        padding: "6px 12px"
      }}>🖨 Printable / email summary</button>
        <button onClick={load} title="Reload the figures">↻</button>
      </div>

      {/* 1 · headline numbers */}
      <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      gap: 10
    }}>
        {kpi("Group in place", model.groupPct == null ? "—" : `${round(model.groupPct)}%`, ch == null ? "trend builds up day by day" : `${ch >= 0 ? "up" : "down"} ${Math.abs(round(ch))} pts since ${fmtDate(trend.past)}`, model.groupPct == null ? null : band(model.groupPct).fg)}
        {kpi("To chase", model.toChase, `${model.people.reduce((s, p) => s + p.open, 0)} requests not answered`, null, () => openOut({}))}
        {kpi("Overdue requests", model.overdue, model.overdue ? `oldest ${model.oldest} day${model.oldest === 1 ? "" : "s"} late` : "none overdue", model.overdue ? "#A8071A" : "#237804", () => openOut({
        type: "awaiting",
        sort: "staff"
      }))}
        {kpi("Renewals in 30 days", model.renewals30, model.renewals30Nobody ? `${model.renewals30Nobody} with no one chosen` : "all have someone to send to", model.renewals30Nobody ? "#AD4E00" : null, () => openOut({
        type: "due"
      }))}
      </div>

      <div style={{
      display: "grid",
      gridTemplateColumns: shows("map") ? "minmax(0, 1.25fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
      gap: 12,
      marginTop: 12
    }} className="cov-two">
        {/* 2 · done / to do by branch — always shown */}
        <div style={card}>
          <div style={h}>Done and still to do, by branch <span style={{
            ...sub,
            fontWeight: 400
          }}>· data items</span></div>
          <Legend items={BUCKETS} />
          {model.per.map(p => <div key={p.b.dbName} style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          margin: "7px 0",
          fontSize: 12
        }}>
              <span style={{
            width: 130,
            color: "#434343",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }} title={name(p.b)}>{name(p.b)}</span>
              <div style={{
            flex: 1,
            display: "flex",
            height: 18,
            gap: 2,
            borderRadius: 4,
            overflow: "hidden",
            background: "#FAFAFA"
          }}>
                {BUCKETS.map(bk => p.buckets[bk.k] ? <div key={bk.k} role="button" tabIndex={0} title={`${name(p.b)} — ${bk.label}: ${p.buckets[bk.k]} of ${p.total}`} onClick={() => bk.type && openOut({
              branch: p.b.dbName,
              type: bk.type
            })} style={{
              width: `${p.buckets[bk.k] / p.total * 100}%`,
              background: bk.color,
              cursor: bk.type ? "pointer" : "default"
            }} /> : null)}
              </div>
              <span style={{
            width: 40,
            textAlign: "right",
            fontWeight: 700,
            color: band(p.pct).fg
          }}>{p.pct == null ? "—" : `${round(p.pct)}%`}</span>
            </div>)}
          <div style={{
          ...sub,
          fontSize: 11,
          marginTop: 6
        }}>The % is the branch’s completion, as on the Working grid headings. The bar counts every data item at the branch.</div>
        </div>

        {/* 6 · map */}
        {shows("map") && <div style={card}>
          <div style={h}>Where the branches are</div>
          <MapGuard><BranchMap per={model.per} meta={meta} name={name} onPick={b => openOut({
            branch: b.dbName
          })} /></MapGuard>
        </div>}
      </div>

      {/* 3 · heatmap */}
      {shows("areas") && <div style={{
      ...card,
      marginTop: 12,
      overflowX: "auto"
    }}>
        <div style={h}>Branch by area — % in place</div>
        <table style={{
        borderCollapse: "separate",
        borderSpacing: 3,
        fontSize: 12,
        minWidth: "100%"
      }}>
          <thead><tr><th style={{
              textAlign: "left",
              color: "#5D5D5D",
              fontWeight: 600,
              padding: "2px 6px"
            }}>Area</th>
            {model.per.map(p => <th key={p.b.dbName} style={{
              color: "#5D5D5D",
              fontWeight: 600,
              padding: "2px 6px",
              whiteSpace: "nowrap"
            }}>{name(p.b)}</th>)}</tr></thead>
          <tbody>{groups.map(g => <tr key={g.id}><td style={{
              padding: "2px 6px",
              whiteSpace: "nowrap",
              color: "#262626"
            }}>{g.name}</td>
              {model.per.map(p => {
              const v = p.groupPct[g.id];
              const c = band(v);
              return <td key={p.b.dbName} title={v == null ? `${g.name} doesn’t apply to ${name(p.b)}` : `${g.name} — ${name(p.b)}: ${round(v)}%. Click for what’s outstanding.`} onClick={() => v != null && openOut({
                branch: p.b.dbName,
                query: g.name
              })} style={{
                background: c.bg,
                color: c.fg,
                textAlign: "center",
                padding: "6px 4px",
                borderRadius: 4,
                fontWeight: 700,
                cursor: v == null ? "default" : "pointer",
                minWidth: 70
              }}>{v == null ? "—" : `${round(v)}%`}</td>;
            })}
            </tr>)}</tbody>
        </table>
      </div>}

      {(shows("coming") || shows("who")) && <div style={{
      display: "grid",
      gridTemplateColumns: shows("coming") && shows("who") ? "minmax(0, 1.3fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
      gap: 12,
      marginTop: 12
    }} className="cov-two">
        {/* 4 · coming up */}
        {shows("coming") && <div style={card}>
          <div style={h}>Coming up — the next 90 days <span style={{
            ...sub,
            fontWeight: 400
          }}>· evidence and registrations that run out</span></div>
          <Timeline per={model.per} name={name} onPick={(p, r) => openOut({
          branch: p.b.dbName,
          query: r.label.split(" — ")[0]
        })} />
        </div>}
        {/* 5 · who */}
        {shows("who") && <div style={card}>
          <div style={h}>Who has open requests</div>
          {!model.people.length ? <div style={{
          ...sub,
          color: "#237804"
        }}>Nobody has a request waiting.</div> : <>
            {model.people.slice(0, 10).map(p => {
            const max = model.people[0].open || 1;
            return <div key={`${p.staffName}|${p.branch.dbName}`} role="button" tabIndex={0} onClick={() => openOut({
              sort: "staff",
              query: p.staffName,
              branch: p.branch.dbName
            })} title={`${p.staffName} (${name(p.branch)}): ${p.open} open, ${p.late} overdue — click to see them`} style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "6px 0",
              fontSize: 12,
              cursor: "pointer"
            }}>
                <span style={{
                width: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>{p.staffName} <span style={{
                  color: "#8C8C8C"
                }}>· {name(p.branch)}</span></span>
                <div style={{
                flex: 1,
                display: "flex",
                height: 12,
                gap: 2
              }}>
                  <div style={{
                  width: `${p.late / max * 100}%`,
                  background: "#CF1322",
                  borderRadius: 3
                }} />
                  <div style={{
                  width: `${(p.open - p.late) / max * 100}%`,
                  background: "#FA8C16",
                  borderRadius: 3
                }} />
                </div>
                <span style={{
                width: 22,
                textAlign: "right",
                fontWeight: 700
              }}>{p.open}</span>
              </div>;
          })}
            <Legend items={[{
            label: "Overdue",
            color: "#CF1322"
          }, {
            label: "Open, not yet due",
            color: "#FA8C16"
          }]} />
            {model.people.length > 10 && <div style={sub}>…and {model.people.length - 10} more — see Outstanding.</div>}
          </>}
        </div>}
      </div>}

      {/* 7 · trend */}
      {shows("trend") && <div style={{
      ...card,
      marginTop: 12
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10
      }}>
          <div style={{
          ...h,
          flex: 1,
          margin: 0
        }}>% in place over time</div>
          <select value={range} onChange={e => setRange(Number(e.target.value))} aria-label="Time range">
            <option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last 12 months</option></select>
        </div>
        <TrendChart points={trend.points} per={model.per} name={name} />
        <div style={{
        ...sub,
        fontSize: 11
      }}>One figure per branch per day, saved whenever this page is opened (today’s is live). Days nobody opened the page are joined up.</div>
      </div>}

      {out && <OutstandingDialog items={outstanding} allItems={evidenceAll} token={token} branches={branches} displayName={displayName} masked={masked} sites={sites} initialBranch={out.branch || "all"} initialType={out.type || "all"} initialSort={out.sort || "branch"} initialQuery={out.query || ""} initialMode={out.mode || "outstanding"} onShowData={renderDetail ? (row, branch) => setDataView({
      row,
      branch
    }) : null} onClose={() => setOut(null)} onChanged={load} />}
      {dataView && renderDetail && <div role="dialog" aria-modal="true" aria-label={`Data — ${dataView.row.label} — ${dataView.branch.pharmacyName}`} onClick={e => {
      if (e.target === e.currentTarget) setDataView(null);
    }} style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,0.5)",
      zIndex: 10050,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "4vh 12px",
      overflow: "auto"
    }}>
        <div style={{
        background: "#fff",
        borderRadius: 10,
        width: "100%",
        maxWidth: 1000,
        boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        overflow: "hidden"
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderBottom: "1px solid #F0F0F0"
        }}>
            <div style={{
            flex: 1
          }}><b>{name(dataView.branch)}</b> <span style={{
              color: "#5D5D5D"
            }}>· {dataView.row.label}</span></div>
            <button onClick={() => setDataView(null)} aria-label="Close">✕</button>
          </div>
          <div style={{
          padding: "0 12px 12px",
          maxHeight: "82vh",
          overflow: "auto"
        }}>{renderDetail(dataView.row, dataView.branch.pharmacyName)}</div>
        </div>
      </div>}
      {summary && <SummaryDialog model={model} groups={groups} outstanding={outstanding} mhra={mhra} trend={trend} name={name} token={token} onClose={() => setSummary(false)} />}
      <style>{`@media (max-width: 1000px) { .cov-two { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
    </div>;
}
function Legend({
  items
}) {
  return <div style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    fontSize: 11,
    color: "#5D5D5D",
    margin: "2px 0 6px"
  }}>
    {items.map(x => <span key={x.label} style={{
      display: "flex",
      alignItems: "center",
      gap: 4
    }}><span style={{
        width: 10,
        height: 10,
        borderRadius: 2,
        background: x.color,
        display: "inline-block"
      }} />{x.label}</span>)}
  </div>;
}

// Renewals on a 90-day line, one lane per branch (past due shown at the left edge).
function Timeline({
  per,
  name,
  onPick
}) {
  const now = Date.now();
  const any = per.some(p => p.renewals.length);
  if (!any) return <div style={{
    fontSize: 12,
    color: "#237804"
  }}>Nothing runs out in the next 90 days.</div>;
  const x = t => Math.max(0, Math.min(100, (t - now) / (90 * DAY) * 100));
  return <div>
    {per.map(p => <div key={p.b.dbName} style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: 26
    }}>
        <span style={{
        width: 120,
        fontSize: 11,
        color: "#5D5D5D",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}>{name(p.b)}</span>
        <div style={{
        flex: 1,
        position: "relative",
        height: 20,
        borderBottom: "1px solid #F0F0F0"
      }}>
          {p.renewals.map((r, i) => {
          const d = Math.round((r.when - now) / DAY);
          const late = d < 0;
          return <button key={i} type="button" onClick={() => onPick(p, r)} aria-label={`${r.label} — ${late ? `expired ${-d} days ago` : `in ${d} days`}`} title={`${r.label}\n${late ? `Expired ${fmtDate(r.when)} (${-d} days ago)` : `Runs out ${fmtDate(r.when)} — in ${d} days`}${r.nobody ? "\nNo one chosen to receive the renewal" : ""}`} style={{
            position: "absolute",
            left: `calc(${x(r.when)}% - 6px)`,
            top: 3,
            width: 13,
            height: 13,
            padding: 0,
            borderRadius: "50%",
            cursor: "pointer",
            background: late ? "#CF1322" : d < 30 ? "#FA8C16" : d < 60 ? "#FAAD14" : "#2a78d6",
            border: r.nobody ? "2px dashed #262626" : "2px solid #fff",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.15)"
          }} />;
        })}
        </div>
      </div>)}
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: 10,
      color: "#8C8C8C",
      marginLeft: 128
    }}><span>Today</span><span>30 days</span><span>60 days</span><span>90 days</span></div>
    <Legend items={[{
      label: "Already run out",
      color: "#CF1322"
    }, {
      label: "Under 30 days",
      color: "#FA8C16"
    }, {
      label: "30–60 days",
      color: "#FAAD14"
    }, {
      label: "60–90 days",
      color: "#2a78d6"
    }]} />
    <div style={{
      fontSize: 11,
      color: "#5D5D5D"
    }}>A dashed ring means no one has been chosen to receive the renewal request.</div>
  </div>;
}

// A map that can't draw (an old browser, a blocked tile server…) never takes the page down with it.
class MapGuard extends React.Component {
  constructor(p) {
    super(p);
    this.state = {
      failed: false
    };
  }
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  componentDidCatch(e) {
    console.warn("[overview map]", e?.message);
  }
  render() {
    return this.state.failed ? <div style={{
      fontSize: 12,
      color: "#5D5D5D"
    }}>The map couldn’t be drawn in this browser.</div> : this.props.children;
  }
}
function FitBounds({
  pts
}) {
  const map = useMap();
  useEffect(() => {
    if (!pts.length) return;
    if (pts.length === 1) map.setView(pts[0], 12);else map.fitBounds(pts, {
      padding: [30, 30],
      maxZoom: 12
    });
  }, [map, pts]);
  return null;
}
export function BranchMap({
  per,
  meta,
  name,
  onPick
}) {
  const placeBy = new Map((meta?.branches || []).map(x => [x.dbName, x.place]));
  const placed = per.filter(p => placeBy.get(p.b.dbName));
  const missing = per.filter(p => !placeBy.get(p.b.dbName));
  const ptsKey = JSON.stringify(placed.map(p => [placeBy.get(p.b.dbName).lat, placeBy.get(p.b.dbName).long]));
  const pts = useMemo(() => JSON.parse(ptsKey), [ptsKey]);
  if (!meta) return <div style={{
    fontSize: 12,
    color: "#5D5D5D"
  }}>Loading locations…</div>;
  return <div>
    {placed.length ? <div style={{
      height: 300,
      borderRadius: 8,
      overflow: "hidden"
    }}>
      <MapContainer center={pts[0]} zoom={9} style={{
        height: "100%",
        width: "100%"
      }} scrollWheelZoom={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
        <FitBounds pts={pts} />
        {placed.map(p => {
          const pl = placeBy.get(p.b.dbName);
          return <CircleMarker key={p.b.dbName} center={[pl.lat, pl.long]} radius={12 + Math.min(10, Math.round(p.chase / 10))} pathOptions={{
            color: "#fff",
            weight: 2,
            fillColor: dotColor(p.pct),
            fillOpacity: 0.9
          }}>
            <Popup><div style={{
                fontSize: 13
              }}><b>{name(p.b)}</b><br />{p.pct == null ? "No figure yet" : `${Math.round(p.pct)}% in place`} · {p.chase} to chase
              {pl.postcode ? <><br /><span style={{
                    color: "#8C8C8C"
                  }}>{pl.postcode}</span></> : null}
              <br /><button style={{
                  marginTop: 6
                }} onClick={() => onPick(p.b)}>Show what’s outstanding</button></div></Popup>
          </CircleMarker>;
        })}
      </MapContainer></div> : <div style={{
      fontSize: 12,
      color: "#5D5D5D"
    }}>No branch has a location on its pharmacy record yet, so there’s nothing to place on the map.</div>}
    <Legend items={[{
      label: "85% or more",
      color: "#389E0D"
    }, {
      label: "65–84%",
      color: "#FA8C16"
    }, {
      label: "Under 65%",
      color: "#CF1322"
    }]} />
    <div style={{
      fontSize: 11,
      color: "#5D5D5D"
    }}>A bigger circle means more to chase.{missing.length ? ` No location on file for: ${missing.map(p => name(p.b)).join(", ")}.` : ""}</div>
  </div>;
}
export function TrendChart({
  points,
  per,
  name
}) {
  if (points.length < 2) return <div style={{
    fontSize: 12,
    color: "#5D5D5D",
    padding: "20px 0"
  }}>Today’s figures are saved now; the line starts to draw from tomorrow.</div>;
  return <div style={{
    marginTop: 12,
    minWidth: 0
  }}>
    <div style={{
      height: 280,
      width: "100%",
      minWidth: 0
    }}>
    <ResponsiveContainer width="100%" height={280} minWidth={0}>
      <LineChart data={points} margin={{
          top: 12,
          right: 20,
          left: 0,
          bottom: 12
        }}>
        <CartesianGrid stroke="#F0F0F0" vertical={false} />
        <XAxis dataKey="label" tick={{
            fontSize: 12,
            fill: "#434343"
          }} minTickGap={40} tickMargin={10} height={40} />
        <YAxis domain={[0, 100]} width={54} tick={{
            fontSize: 12,
            fill: "#434343"
          }} tickMargin={8} tickFormatter={v => `${v}%`} />
        <Tooltip formatter={(v, k) => [`${Math.round(v)}%`, k === "group" ? "Whole group" : name(per.find(p => p.b.dbName === k)?.b || {
            pharmacyName: k
          })]} />
        {per.map((p, i) => <Line key={p.b.dbName} type="monotone" dataKey={p.b.dbName} stroke={SERIES[i % SERIES.length]} strokeWidth={1.5} dot={false} connectNulls />)}
        <Line type="monotone" dataKey="group" stroke="#262626" strokeWidth={3} dot={false} connectNulls />
      </LineChart>
    </ResponsiveContainer>
    </div>
    <div role="list" aria-label="Chart legend" style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "10px 24px",
      padding: "12px 0 16px",
      fontSize: 13,
      lineHeight: "20px",
      color: "#262626",
      clear: "both"
    }}>
      {[{
        label: "Whole group",
        color: "#262626",
        key: "group"
      }, ...per.map((p, i) => ({
        label: name(p.b),
        color: SERIES[i % SERIES.length],
        key: p.b.dbName
      }))].map(series => <div role="listitem" key={series.key} style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        maxWidth: "100%",
        minWidth: 0
      }}>
          <span aria-hidden="true" style={{
          width: 24,
          height: 4,
          flexShrink: 0,
          borderRadius: 2,
          background: series.color
        }} />
          <span style={{
          overflowWrap: "anywhere",
          fontWeight: series.key === "group" ? 700 : 500
        }}>{series.label}</span>
        </div>)}
    </div>
  </div>;
}

// -----------------------------------------------------------------------------
// Printable / emailable summary — the whole group, or one branch.
// -----------------------------------------------------------------------------
function summaryHtml({
  scope,
  model,
  groups,
  outstanding,
  mhra,
  trend,
  name
}) {
  const per = scope === "all" ? model.per : model.per.filter(p => p.b.dbName === scope);
  const title = scope === "all" ? "Compliance summary — whole group" : `Compliance summary — ${name(per[0].b)}`;
  const pct = scope === "all" ? model.groupPct : per[0].pct;
  const items = [...outstanding.filter(x => scope === "all" || x.branch.dbName === scope), ...mhra.filter(m => scope === "all" || m.dbName === scope).map(m => ({
    branch: model.per.find(p => p.b.dbName === m.dbName)?.b,
    type: "mhra",
    groupName: "Medicines & Services",
    element: `MHRA alert: ${m.title}`,
    staffName: m.staffName,
    what: `Not read yet${m.overdue ? " — overdue" : ""}`
  }))].filter(x => x.branch);
  const typeLabel = Object.fromEntries(OUT_TYPES);
  const byType = {};
  for (const x of items) byType[x.type] = (byType[x.type] || 0) + 1;
  const renewals = per.flatMap(p => p.renewals.map(r => ({
    ...r,
    b: p.b
  }))).filter(r => r.when - Date.now() <= 90 * DAY).sort((a, b) => a.when - b.when);
  const cell = v => {
    const c = band(v);
    return `<td style="background:${c.bg};color:${c.fg};text-align:center;font-weight:bold;padding:4px 6px">${v == null ? "—" : `${Math.round(v)}%`}</td>`;
  };
  const th = 'style="text-align:left;padding:4px 6px;border-bottom:1px solid #d9d9d9;font-size:12px;color:#595959"';
  const td = 'style="padding:4px 6px;border-bottom:1px solid #f0f0f0;font-size:12px;vertical-align:top"';
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;color:#141414;max-width:900px">
  <h1 style="font-size:20px;margin:0 0 2px">${esc(title)}</h1>
  <div style="color:#595959;font-size:12px;margin-bottom:12px">${esc(new Date().toLocaleString("en-GB", {
    dateStyle: "long",
    timeStyle: "short"
  }))}</div>
  <table style="border-collapse:collapse;margin-bottom:14px"><tr>
    <td style="padding:8px 14px;background:#fafafa;border:1px solid #f0f0f0"><div style="font-size:11px;color:#595959">In place</div><div style="font-size:24px;font-weight:bold;color:${band(pct).fg}">${pct == null ? "—" : `${Math.round(pct)}%`}</div>
      ${scope === "all" && trend.change != null ? `<div style="font-size:11px;color:#595959">${trend.change >= 0 ? "up" : "down"} ${Math.abs(Math.round(trend.change))} pts since ${esc(fmtDate(trend.past))}</div>` : ""}</td>
    <td style="padding:8px 14px;background:#fafafa;border:1px solid #f0f0f0"><div style="font-size:11px;color:#595959">To chase</div><div style="font-size:24px;font-weight:bold">${items.length}</div></td>
    <td style="padding:8px 14px;background:#fafafa;border:1px solid #f0f0f0"><div style="font-size:11px;color:#595959">Renewals in 90 days</div><div style="font-size:24px;font-weight:bold">${renewals.length}</div></td>
  </tr></table>
  <h2 style="font-size:15px;margin:14px 0 6px">By area — % in place</h2>
  <table style="border-collapse:separate;border-spacing:2px"><tr><th ${th}>Area</th>${per.map(p => `<th ${th}>${esc(name(p.b))}</th>`).join("")}</tr>
    ${groups.filter(g => per.some(p => p.groupPct[g.id] != null)).map(g => `<tr><td ${td}>${esc(g.name)}</td>${per.map(p => cell(p.groupPct[g.id])).join("")}</tr>`).join("")}
    <tr><td ${td}><b>Overall</b></td>${per.map(p => cell(p.pct)).join("")}</tr></table>
  <h2 style="font-size:15px;margin:14px 0 6px">What needs chasing (${items.length})</h2>
  <div style="font-size:12px;color:#595959;margin-bottom:4px">${Object.entries(byType).map(([k, n]) => `${esc(typeLabel[k] || k)}: ${n}`).join(" · ") || "Nothing outstanding."}</div>
  ${items.length ? `<table style="border-collapse:collapse;width:100%"><tr>${scope === "all" ? `<th ${th}>Branch</th>` : ""}<th ${th}>Item</th><th ${th}>Staff member</th><th ${th}>What’s wrong</th></tr>
    ${items.slice(0, 150).map(x => `<tr>${scope === "all" ? `<td ${td}>${esc(name(x.branch))}</td>` : ""}<td ${td}><span style="color:#8c8c8c">${esc(x.groupName)}</span><br>${esc(x.element)}</td><td ${td}>${esc(x.staffName || "—")}</td><td ${td}><b>${esc(typeLabel[x.type] || x.type)}</b> — ${esc(x.what)}</td></tr>`).join("")}</table>
    ${items.length > 150 ? `<div style="font-size:12px;color:#595959">…and ${items.length - 150} more.</div>` : ""}` : ""}
  <h2 style="font-size:15px;margin:14px 0 6px">Coming up — next 90 days</h2>
  ${renewals.length ? `<table style="border-collapse:collapse;width:100%"><tr><th ${th}>Runs out</th>${scope === "all" ? `<th ${th}>Branch</th>` : ""}<th ${th}>Item</th></tr>
    ${renewals.map(r => `<tr><td ${td}>${esc(fmtDate(r.when))}${r.when < Date.now() ? " <b style=\"color:#a8071a\">(expired)</b>" : ""}</td>${scope === "all" ? `<td ${td}>${esc(name(r.b))}</td>` : ""}<td ${td}>${esc(r.label)}${r.nobody ? " <span style=\"color:#ad4e00\">— no one chosen</span>" : ""}</td></tr>`).join("")}</table>` : `<div style="font-size:12px">Nothing runs out in the next 90 days.</div>`}
  <div style="font-size:11px;color:#8c8c8c;margin-top:16px">From Pharmace — Regulatory compliance → Overview.</div></div>`;
  const text = `${title}\n${new Date().toLocaleString("en-GB")}\nIn place: ${pct == null ? "—" : `${Math.round(pct)}%`} · To chase: ${items.length} · Renewals in 90 days: ${renewals.length}\n\n` + items.slice(0, 150).map(x => `${scope === "all" ? `${name(x.branch)} — ` : ""}${x.element} — ${x.staffName || "—"} — ${typeLabel[x.type] || x.type}: ${x.what}`).join("\n");
  return {
    title,
    html,
    text
  };
}
function SummaryDialog({
  model,
  groups,
  outstanding,
  mhra,
  trend,
  name,
  token,
  onClose
}) {
  const [scope, setScope] = useState("all");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false),
    [msg, setMsg] = useState(""),
    [err, setErr] = useState("");
  const doc = useMemo(() => summaryHtml({
    scope,
    model,
    groups,
    outstanding,
    mhra,
    trend,
    name
  }), [scope, model, groups, outstanding, mhra, trend, name]);
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) {
      setErr("Your browser blocked the print window — allow pop-ups for this site and try again.");
      return;
    }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(doc.title)}</title><style>@page{margin:14mm}body{margin:0}</style></head><body>${doc.html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };
  const email = async () => {
    setErr("");
    setMsg("");
    if (!to.trim()) {
      setErr("Enter at least one email address");
      return;
    }
    setBusy(true);
    try {
      const r = await post(token, `${GRID}/overview/email`, {
        to,
        subject: doc.title,
        html: doc.html,
        text: doc.text
      });
      const ok = r.results.filter(x => x.status === "sent").length;
      setMsg(`Sent to ${ok} of ${r.results.length}${ok < r.results.length ? ` — failed: ${r.results.filter(x => x.status !== "sent").map(x => x.to).join(", ")}` : ""}.`);
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };
  return <div role="dialog" aria-modal="true" aria-label="Compliance summary" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    zIndex: 10000,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "3vh 12px",
    overflow: "auto"
  }}>
      <div style={{
      background: "#fff",
      borderRadius: 10,
      width: "100%",
      maxWidth: 980,
      boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
      overflow: "hidden"
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderBottom: "1px solid #F0F0F0",
        flexWrap: "wrap"
      }}>
          <div style={{
          fontWeight: 800,
          fontSize: 16,
          flex: 1
        }}>🖨 Printable / email summary</div>
          <label style={{
          fontSize: 13
        }}>For <select value={scope} onChange={e => setScope(e.target.value)}>
            <option value="all">The whole group</option>
            {model.per.map(p => <option key={p.b.dbName} value={p.b.dbName}>{name(p.b)}</option>)}</select></label>
          <button onClick={print}>🖨 Print</button>
          <button onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        padding: "10px 16px",
        background: "#FAFAFA",
        borderBottom: "1px solid #F0F0F0",
        flexWrap: "wrap"
      }}>
          <input value={to} onChange={e => {
          setTo(e.target.value);
          setErr("");
        }} placeholder="manager@example.com, area.manager@example.com" style={{
          flex: 1,
          minWidth: 260
        }} aria-label="Email addresses" />
          <button disabled={busy} onClick={email} style={{
          fontWeight: 700,
          background: "#0958D9",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          padding: "6px 12px"
        }}>{busy ? "Sending…" : "✉ Email it"}</button>
          {err && <span style={{
          color: "#A8071A",
          fontSize: 13
        }}>{err}</span>}
          {msg && <span style={{
          color: "#237804",
          fontSize: 13,
          fontWeight: 600
        }}>{msg}</span>}
        </div>
        <div style={{
        padding: 16,
        maxHeight: "70vh",
        overflow: "auto"
      }} dangerouslySetInnerHTML={{
        __html: doc.html
      }} />
      </div>
    </div>;
}
